import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Layout, Button } from 'antd';
import { DoubleLeftOutlined, DoubleRightOutlined } from '@ant-design/icons';
// import { Outlet } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { deviceQueries, isSmallDevice as colapsedOnSmallDevice } from '@/config/theme';
import styles from './styles.module.scss';
import SiderMenu from './Menu';
import MainHeader from './Header';
import { useAuth } from '@/context/AuthContext';
import AccountDetails from './components/AccountDetails';
import { ChatWidget } from '@/components/Chat';
import { isPublicTokenPath } from '@/utils/api';
import { collapsedAfterBreakpoint, collapsedOnLeave, collapsesSider } from './fullWidth';
const { Content, Sider } = Layout;

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(colapsedOnSmallDevice());
  const { auth } = useAuth();
  const { pathname } = useLocation();

  const isInstallerRoute = pathname.startsWith('/installer/');
  const isPublicTokenView = isPublicTokenPath(pathname);

  const { isAuthenticated } = auth || {};

  // Use react-responsive for device detection
  const isSmallDevice = useMediaQuery({ query: deviceQueries.smallDevice });

  // Full-width routes (the proposal editor): collapse the sider while open, give the
  // rep's own choice back on leaving. The toggle still works in between.
  const fullWidth = collapsesSider(pathname);
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;
  const fullWidthRef = useRef(fullWidth);
  fullWidthRef.current = fullWidth;
  const isSmallRef = useRef(isSmallDevice);
  isSmallRef.current = isSmallDevice;

  // Set initial collapsed state based on device (a full-width route keeps it collapsed).
  useEffect(() => {
    setCollapsed(collapsedAfterBreakpoint(isSmallDevice, fullWidthRef.current));
  }, [isSmallDevice]);

  useEffect(() => {
    if (!fullWidth) return;
    const before = collapsedRef.current;
    const smallAtEntry = isSmallRef.current;
    setCollapsed(true);
    // Review M8: if the window crossed the breakpoint meanwhile, the current size's default instead.
    return () => setCollapsed(collapsedOnLeave({ before, smallAtEntry, smallNow: isSmallRef.current }));
  }, [fullWidth]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  const handleOverlayClick = useCallback(() => {
    if (isSmallDevice) {
      setCollapsed(true);
    }
  }, [isSmallDevice]);



  const collapsedWidth = useMemo(() => {
    if (isSmallDevice) return 0;
    return undefined;
  }, [isSmallDevice]);

  if (isInstallerRoute || isPublicTokenView) {
    return <>{children}</>;
  }

  return (
    <Layout hasSider={!isSmallDevice && isAuthenticated} style={{ minHeight: '100%' }}>
      {isSmallDevice && !collapsed && (
        <div className={styles.overlay} onClick={handleOverlayClick} />
      )}

      { isAuthenticated && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={256}
          className={styles.sider}
          collapsedWidth={collapsedWidth}
      >
        <div className={cn(styles.siderHeader, { [styles.collapsed]: collapsed })}>
          {!collapsed && <h1 className={styles.headerTitle}>Partner Portal</h1>}
          <Button
            type="text"
            icon={collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
            onClick={toggleCollapsed}
          />
        </div>
        {
          !collapsed && auth?.user && (
            <div className={styles.accountDetails}>
              <AccountDetails accountName={auth?.user.accountName} accountId={auth?.user.accountId} />
            </div>
          )
        }
          <SiderMenu isOpen={!collapsed} onSelect={toggleCollapsed} />
        </Sider>
      )}

      <Layout>
        {isAuthenticated && (
          <MainHeader isSmallDevice={isSmallDevice} collapsed={collapsed} onBurgerClick={toggleCollapsed} />
        )}

        <Content className={cn({ [styles.content]: isAuthenticated })}>
          {/* <Outlet /> */}
          {children}
        </Content>
      </Layout>
      {isAuthenticated && auth?.user?.role === 'SUPER_ADMIN' && <ChatWidget />}
    </Layout>
  );
};

export default MainLayout;
